import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShinyText } from '../ui/shiny-text';
import { PulsatingButton } from '../ui/pulsating-button';
import Card from '../Card';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import AuthModal from '../auth/AuthModal';
import SubscriptionModal from '../subscription/SubscriptionModal';
import UpdatePaymentMethodModal from '../subscription/UpdatePaymentMethodModal';
import { CardValue, Suit } from '../../types/poker';
import { useTawkTo } from '../../hooks/useTawkTo';
import { tawkToConfig } from '../../config/tawkto';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { hasActiveSubscription, hasPastDueSubscription, checkSubscriptionStatus, hasHadTrial, checkTrialStatus } = useSubscription();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  // State for subscription processing
  const [isSubscriptionProcessing, setIsSubscriptionProcessing] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [pollingMessage, setPollingMessage] = useState('Processing your subscription...');
  
  // Add a ref to track the interval ID and prevent multiple instances
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Add a ref to track if component is mounted to prevent updates after unmount
  const isMountedRef = useRef(true);
  // Add a ref to track polling attempts to ensure accurate incrementing
  const pollingAttemptsRef = useRef(0);
  
  // Constants for polling
  const POLLING_INTERVAL = 5000; // 5 seconds - increased from 2 seconds
  
  // Initialize tawk.to for authenticated users on landing page
  const tawkToControls = useTawkTo({
    propertyId: tawkToConfig.propertyId,
    widgetId: tawkToConfig.widgetId,
    enabled: tawkToConfig.features.enabled && tawkToConfig.features.showOnLanding,
    customAttributes: {
      ...tawkToConfig.defaultAttributes,
      page: 'landing',
      userType: hasActiveSubscription ? 'subscriber' : (hasHadTrial ? 'trial_used' : 'trial_eligible'),
    },
    onLoad: () => {
      console.log('Tawk.to loaded on landing page');
      // Inject custom CSS for dark theme
      if (tawkToConfig.styling.customCSS) {
        const style = document.createElement('style');
        style.textContent = tawkToConfig.styling.customCSS;
        document.head.appendChild(style);
      }
    },
  });

  // Define the polling function
  const pollSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated || !user?.token) {
      console.log('Not authenticated or no token, aborting poll');
      return false;
    }
    
    try {
      // Use the ref for counting attempts to avoid closure issues
      pollingAttemptsRef.current += 1;
      const currentAttempt = pollingAttemptsRef.current;
      
      // Log the current attempt number
      console.log(`Polling subscription status (attempt ${currentAttempt})...`);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setPollingAttempts(currentAttempt);
      }
      
      // Force check with the server - this MUST be true to bypass caching
      const hasSubscription = await checkSubscriptionStatus(true);
      
      console.log(`Subscription status check result: ${hasSubscription ? 'Active' : 'Not active yet'}`);
      
      if (hasSubscription) {
        console.log('Subscription is now active after polling, redirecting to app');
        
        // Clean up polling before navigating
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsSubscriptionProcessing(false);
          // Navigate to app
          navigate('/app');
        }
        return true;
      }
      
      // Update the polling message based on attempts
      if (currentAttempt > 5 && isMountedRef.current) {
        setPollingMessage(hasHadTrial
          ? 'Still processing your subscription. This may take a moment...'
          : 'Still setting up your free trial. This may take a moment...');
      }
      if (currentAttempt > 15 && isMountedRef.current) {
        setPollingMessage(hasHadTrial
          ? 'Your subscription is being set up. Thank you for your patience...'
          : 'Your free trial is being set up. Thank you for your patience...');
      }
      
      // If we've tried too many times, stop polling and show a message
      if (currentAttempt >= 30 && isMountedRef.current) {
        console.log('Maximum polling attempts reached, stopping polling');
        setPollingMessage('Please refresh the page or check your account status.');
        
        // Clean up polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        setIsSubscriptionProcessing(false);
      }
      
      return false;
    } catch (error) {
      console.error('Error polling subscription status:', error);
      // Don't stop polling on error, just return false to continue
      return false;
    }
  }, [isAuthenticated, user?.token, checkSubscriptionStatus, navigate, hasHadTrial]);

  // Check subscription status and trial status on component mount
  useEffect(() => {
    const checkStatus = async () => {
      if (isAuthenticated && user?.token) {
        setIsCheckingStatus(true);
        try {
          // Check if the user has an active subscription, using cached value if available
          const hasSubscription = await checkSubscriptionStatus(false);
          
          // Also check trial status
          const hadTrial = await checkTrialStatus();
          
          console.log(`User authentication status: Authenticated, Has subscription: ${hasSubscription}, Has had trial: ${hadTrial}`);
          
          if (hasSubscription) {
            navigate('/app');
          } else if (sessionStorage.getItem('just_created_subscription') === 'true') {
            // If we just created a subscription but it's not showing as active yet,
            // start the polling process to check for subscription status
            console.log('User just created a subscription, starting polling process');
            sessionStorage.removeItem('just_created_subscription');
            
            // Reset polling state
            pollingAttemptsRef.current = 0;
            setPollingAttempts(0);
            setPollingMessage(hadTrial 
              ? 'Processing your subscription...' 
              : 'Setting up your free trial...');
            
            // Start the polling process
            setIsSubscriptionProcessing(true);
          }
        } catch (error) {
          console.error('Error checking subscription status:', error);
        } finally {
          setIsCheckingStatus(false);
        }
      }
    };
    
    checkStatus();
  }, [isAuthenticated, user, checkSubscriptionStatus, navigate, checkTrialStatus]);

  // Effect for subscription polling - improve this to prevent multiple instances
  useEffect(() => {
    // Set mount state on component mount
    isMountedRef.current = true;
    
    // Store the polling state in a local variable to avoid dependency issues
    const isPolling = isSubscriptionProcessing && isAuthenticated;
    
    if (isPolling) {
      console.log('Starting subscription status polling...');
      
      // Reset polling attempts ONLY when starting polling for the first time
      if (pollingIntervalRef.current === null) {
        console.log('Initializing new polling sequence, resetting counter');
        pollingAttemptsRef.current = 0;
        if (isMountedRef.current) {
          setPollingAttempts(0);
        }
      }
      
      // Clear any existing interval first
      if (pollingIntervalRef.current) {
        console.log('Clearing existing polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Run immediately on start - but use setTimeout to avoid immediate API call
      // This helps prevent double calls when the component rerenders
      const initialCheckTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          console.log('Running initial subscription check...');
          pollSubscriptionStatus();
          
          // Only start the interval after the initial check and a delay
          if (isMountedRef.current) {
            pollingIntervalRef.current = setInterval(() => {
              if (isMountedRef.current) {
                pollSubscriptionStatus();
              }
            }, POLLING_INTERVAL);
          }
        }
      }, 1000); // 1-second delay before first check
      
      // Cleanup function should clear both the interval and the timeout
      return () => {
        if (pollingIntervalRef.current) {
          console.log('Component unmounting, cleaning up polling interval');
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        clearTimeout(initialCheckTimeout);
        // Mark component as unmounted
        isMountedRef.current = false;
      };
    } else if (!isPolling && pollingIntervalRef.current) {
      // If we're not processing but have an interval, clean it up
      console.log('Subscription processing ended, clearing polling interval');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      
      return () => {
        // Mark component as unmounted
        isMountedRef.current = false;
      };
    }
    
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
    };
  }, [isSubscriptionProcessing, isAuthenticated, pollSubscriptionStatus]);

  // This effect specifically watches for subscription changes via localStorage
  useEffect(() => {
    // Check if a subscription was just created (set by SubscriptionModal)
    if (localStorage.getItem('has_active_subscription') === 'true') {
      console.log('Detected newly created subscription flag, verifying with server...');
      
      // Clear the flag immediately to prevent future unwanted redirects
      localStorage.removeItem('has_active_subscription');
      
      // Verify with the server that the subscription is actually active
      const verifySubscription = async () => {
        try {
          // Force check to bypass cache
          const hasSubscription = await checkSubscriptionStatus(true);
          
          if (hasSubscription) {
            console.log('Server confirmed subscription is active, redirecting to app');
            navigate('/app');
          } else {
            console.warn('Server reports no active subscription despite localStorage flag');
            // The subscription might still be processing, don't redirect
          }
        } catch (error) {
          console.error('Error verifying subscription with server:', error);
        }
      };
      
      verifySubscription();
    }
  }, [navigate, checkSubscriptionStatus]);

  // Handle the free trial flow
  const handleStartFreeTrial = () => {
    // Only set starting_free_trial if user hasn't had a trial
    if (!hasHadTrial) {
      sessionStorage.setItem('starting_free_trial', 'true');
    } else {
      sessionStorage.removeItem('starting_free_trial');
    }
    
    if (isAuthenticated) {
      setShowSubscriptionModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  // Handle the login flow (when user clicks "Log In")
  const handleLogin = () => {
    // No need to store login intent anymore
    setShowAuthModal(true);
  };

  // Check if user is returning from auth with intent to start free trial
  useEffect(() => {
    if (isAuthenticated && sessionStorage.getItem('starting_free_trial') === 'true') {
      sessionStorage.removeItem('starting_free_trial');
      if (!hasActiveSubscription) {
        setShowSubscriptionModal(true);
      } else {
        navigate('/app');
      }
    }
  }, [isAuthenticated, hasActiveSubscription, navigate]);

  // Handle subscription creation
  const handleSubscriptionCreated = () => {
    console.log('Subscription created, starting polling process');
    
    // Set a flag in sessionStorage to indicate we just created a subscription
    sessionStorage.setItem('just_created_subscription', 'true');
    
    // Close the subscription modal
    setShowSubscriptionModal(false);
    
    // Reset polling state when explicitly starting a new subscription process
    pollingAttemptsRef.current = 0;
    setPollingAttempts(0);
    setPollingMessage(hasHadTrial 
      ? 'Processing your subscription...' 
      : 'Setting up your free trial...');
    
    // Start the polling process
    setIsSubscriptionProcessing(true);
    
    // Force an immediate check to potentially catch quick subscription activations
    setTimeout(() => {
      checkSubscriptionStatus(true).then(hasSubscription => {
        console.log(`Immediate subscription check result: ${hasSubscription ? 'Active' : 'Not active yet'}`);
        
        // If the subscription is active, navigate to the app
        if (hasSubscription) {
          setIsSubscriptionProcessing(false);
          navigate('/app');
        }
        // Remove the forced navigation for trial users - rely on polling instead
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
      {/* Header */}
      <header className="fixed w-full top-0 bg-black/50 backdrop-blur-sm z-50">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-medium text-gray-300">WrapPractice by</span>
            <div className="text-3xl font-bold">
              <ShinyText>NestSim™</ShinyText>
            </div>
          </div>
          <div>
            {!isAuthenticated ? (
              // User is not authenticated - show Sign In button
              <button
                onClick={handleLogin}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Sign In
              </button>
            ) : hasActiveSubscription ? (
              // User is authenticated and has an active subscription - show Go to App button
              <button
                onClick={() => navigate('/app')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Go to App
              </button>
            ) : (
              // User is authenticated but doesn't have an active subscription - show Subscribe button
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Subscribe
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Authentication Loading Overlay */}
      {isAuthenticating && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Signing You In</h2>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-6"></div>
            <p className="text-white mb-4">Checking your account status...</p>
            <p className="text-gray-400 text-sm">
              This will only take a moment.
            </p>
          </div>
        </div>
      )}

      {/* Subscription Processing Overlay */}
      {isSubscriptionProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Setting Up Your Subscription</h2>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-6"></div>
            <p className="text-white mb-4">{pollingMessage}</p>
            <p className="text-gray-400 text-sm mb-2">
              This may take a few moments. We'll redirect you as soon as your account is ready.
            </p>
            <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden mb-2">
              <div 
                className="bg-blue-500 h-full transition-all duration-500 ease-out" 
                style={{ 
                  width: `${Math.min(100, (pollingAttempts / 30) * 100)}%` 
                }}
              ></div>
            </div>
            <p className="text-xs text-gray-500">Checking subscription status...</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto text-center">
          {/* Trademark-compliant branding */}
          <div className="mb-4">
            <span className="text-2xl md:text-3xl font-medium text-gray-300">WrapPractice by </span>
            <span className="text-4xl md:text-5xl font-bold">
              <ShinyText>NestSim™</ShinyText>
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <ShinyText>Master Pot Limit Omaha Wraps</ShinyText>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Improve your PLO game by mastering wrap identification with our interactive practice tool
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            {!isAuthenticated ? (
              // User is not authenticated - show both Login and Free Trial buttons
              <>
                <button
                  onClick={() => handleLogin()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-colors text-lg font-medium"
                >
                  Log In
                </button>
                <div className="hidden sm:flex items-center px-2">
                  <div className="h-8 w-px bg-gray-500"></div>
                </div>
                <div className="mt-8 sm:mt-0 flex flex-col items-center">
                  <PulsatingButton
                    onClick={handleStartFreeTrial}
                    style={{ fontSize: '1.25rem', padding: '0.75rem 2rem' }}
                  >
                    {!hasHadTrial ? 'Start 2-Day Free Trial' : 'Subscribe Now'}
                  </PulsatingButton>
                  <p className="mt-2 text-gray-400 text-sm">
                    {!hasHadTrial 
                      ? 'Credit card required for trial, no charge for 2 days' 
                      : 'Unlock full access for just $9.99/month'}
                  </p>
                </div>
              </>
            ) : hasActiveSubscription ? (
              // User is authenticated and has an active subscription - show Go to App button
              <button
                onClick={() => navigate('/app')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg transition-colors text-lg font-medium"
              >
                Go to App
              </button>
            ) : hasPastDueSubscription ? (
              // User is authenticated with a past_due subscription - show payment update alert
              <div className="flex flex-col items-center max-w-lg mx-auto">
                <div className="bg-red-900/50 border-2 border-red-500 rounded-lg p-6 mb-4 w-full">
                  <div className="flex items-start">
                    <svg className="w-8 h-8 text-red-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Payment Failed
                      </h3>
                      <p className="text-gray-200 mb-4">
                        Your subscription payment could not be processed. 
                        Update your payment method to restore access to your account.
                      </p>
                      <PulsatingButton
                        onClick={() => setShowUpdatePaymentModal(true)}
                        style={{ width: '100%', backgroundColor: '#DC2626' }}
                      >
                        Update Payment Method
                      </PulsatingButton>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // User is authenticated but doesn't have an active subscription - show Subscribe button
              <div className="flex flex-col items-center">
                <PulsatingButton
                  onClick={() => setShowSubscriptionModal(true)}
                  style={{ fontSize: '1.25rem', padding: '0.75rem 2rem' }}
                >
                  {!hasHadTrial ? 'Start 2-Day Free Trial' : 'Subscribe Now'}
                </PulsatingButton>
                <p className="mt-2 text-gray-400 text-sm">
                  {!hasHadTrial 
                    ? 'Credit card required for trial, no charge for 2 days' 
                    : 'Unlock full access for just $9.99/month'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <ShinyText>See It In Action</ShinyText>
          </h2>
          <p className="text-gray-400 mb-10 max-w-xl mx-auto">
            Watch a quick demo to see how WrapPractice helps you master PLO wraps
          </p>
          <div className="flex justify-center">
            <div className="w-full max-w-[360px] aspect-[9/16] rounded-xl overflow-hidden shadow-2xl shadow-blue-500/10">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/Qfk6GF8DIr8?rel=0&modestbranding=1"
                title="WrapPractice Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section className="py-20 px-4 bg-gray-900/50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <ShinyText>Interactive Practice</ShinyText>
          </h2>
          <div className="flex justify-center items-center gap-4 flex-wrap">
            <div className="bg-black/50 p-8 rounded-xl backdrop-blur-sm max-w-md">
              <h3 className="text-xl font-semibold mb-4">Real-time Feedback</h3>
              <p className="text-gray-300 mb-4">
                Practice with randomly generated hands and get instant feedback on your wrap identification skills
              </p>
              <div className="flex justify-center gap-2">
                {/* Example cards */}
                <Card card={{ value: CardValue.ACE, suit: Suit.HEARTS }} />
                <Card card={{ value: CardValue.KING, suit: Suit.SPADES }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Simple Pricing</h2>
          <div className="max-w-md mx-auto bg-gray-800 rounded-lg overflow-hidden shadow-lg">
            <div className="px-6 py-8">
              <h3 className="text-2xl font-bold text-center">Premium Access</h3>
              <div className="mt-4 text-center">
                <span className="text-5xl font-bold">$9.99</span>
                <span className="text-gray-400">/month</span>
              </div>
              {!hasHadTrial && (
                <div className="mt-2 text-center">
                  <span className="inline-block bg-blue-600 text-white text-sm px-3 py-1 rounded-full">
                    Includes 2-day free trial
                  </span>
                </div>
              )}
              <ul className="mt-6 space-y-4">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span>Unlimited practice sessions</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Performance tracking
                </li>
              </ul>
              <div className="mt-8">
                <button 
                  onClick={handleStartFreeTrial}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
                >
                  {!hasHadTrial ? 'Start Free Trial' : 'Subscribe Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 py-8">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <div className="flex items-center justify-center gap-3">
            <img src="/images/NestSim_logo_128px.png" alt="Nestsim LLC Logo" />
            <p>&copy; {new Date().getFullYear()} Nestsim LLC™. All rights reserved.</p>
          </div>
          <div className="mt-4 space-x-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          onSuccess={async () => {
            setShowAuthModal(false);
            setIsAuthenticating(true);
            
            // Wait a brief moment for auth context to update
            setTimeout(async () => {
              try {
                // Check subscription status to determine what to show next
                const hasSubscription = await checkSubscriptionStatus(true);
                
                if (hasSubscription) {
                  // User already has subscription, redirect to app
                  navigate('/app');
                } else {
                  // User needs to subscribe, show subscription modal
                  setShowSubscriptionModal(true);
                }
              } catch (error) {
                console.error('Error checking subscription status after auth:', error);
                // On error, default to showing subscription modal
                setShowSubscriptionModal(true);
              } finally {
                setIsAuthenticating(false);
              }
            }, 1000);
          }}
          message={!hasHadTrial ? "Sign in to start your free trial" : "Sign in to subscribe"}
        />
      )}
      {showSubscriptionModal && (
        <SubscriptionModal 
          onClose={() => setShowSubscriptionModal(false)}
          onSubscriptionCreated={handleSubscriptionCreated}
        />
      )}
      {showUpdatePaymentModal && (
        <UpdatePaymentMethodModal
          onClose={() => setShowUpdatePaymentModal(false)}
          onSuccess={async () => {
            // After successful payment method update, check subscription status
            console.log('Payment method updated, checking subscription status...');
            const hasActive = await checkSubscriptionStatus(true);
            
            // If subscription is now active, navigate to app
            if (hasActive) {
              console.log('Subscription reactivated, redirecting to app');
              navigate('/app');
            }
          }}
        />
      )}
    </div>
  );
};

export default LandingPage; 